import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/Card";
import Button from "components/Button";
import { 
  getManagerAvailableCyclesAPI,
  getManagerCycleEmployeesStatusAPI,
  getManagerTeamMemberResponsesAPI,
  createPeerReviewNominationsAPI,
  getPeerReviewNominationsAPI,
  deletePeerReviewNominationAPI,
  getAllEmployeesForReviewsAssignment
} from "api/apiHelper";
import { toast } from "react-toastify";
import { MdEdit, MdCheckCircle, MdPending, MdExpandMore, MdExpandLess, MdPictureAsPdf, MdAdd } from "react-icons/md";
import { generateAppraisalPDF } from "utils/reportGenerator";

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  photo?: string;
  email?: string;
  designation?: string;
}

interface CycleWithStatus {
  id: number;
  name: string;
  enable_manager_review: boolean;
  enable_peer_review: boolean;
  enable_client_review: boolean;
  completed: Employee[];
  pending: Employee[];
}

interface AppraisalResponse {
  id: number;
  reviewer_type: string;
  status: string;
  created_at: string;
  question_responses?: any[];
  reviewer_name?: string;
}

const ManagerReviewTab = () => {
  const navigate = useNavigate();
  const modalRef = useRef<HTMLDivElement>(null);
  const [cycleData, setCycleData] = useState<CycleWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [employeeResponses, setEmployeeResponses] = useState<Record<string, AppraisalResponse[]>>({});
  const [loadingResponses, setLoadingResponses] = useState<Record<string, boolean>>({});
  const [generatingPdf, setGeneratingPdf] = useState<Record<string, boolean>>({});
  const [showNominationModal, setShowNominationModal] = useState(false);
  const [selectedReviewee, setSelectedReviewee] = useState<Employee | null>(null);
  const [selectedCycle, setSelectedCycle] = useState<CycleWithStatus | null>(null);
  const [selectedReviewers, setSelectedReviewers] = useState<number[]>([]);
  const [managerTeamMembers, setManagerTeamMembers] = useState<Employee[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [existingNominations, setExistingNominations] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const normalizeEmployee = (employee: any): Employee => ({
    id: employee.id,
    first_name: employee.first_name || '',
    last_name: employee.last_name || '',
    full_name: employee.full_name || `${employee.first_name || ''} ${employee.last_name || ''}`.trim() || 'Unknown',
    photo: employee.photo,
    email: employee.email,
    designation: employee.designation,
  });

  useEffect(() => {
    fetchManagerReviews();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleCloseNominationModal();
      }
    };

    if (showNominationModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [showNominationModal]);

  const fetchManagerReviews = async () => {
    setLoading(true);
    try {
      const res = await getManagerAvailableCyclesAPI();
      const cyclesWithStatus = await Promise.all(
        (res.data || []).map(async (cycle: any) => {
          try {
            const statusResponse = await getManagerCycleEmployeesStatusAPI(cycle.id);
            const completed = statusResponse.data.completed || [];
            const pending = statusResponse.data.pending || [];
            
            return {
              id: cycle.id,
              name: cycle.name,
              enable_manager_review: !!cycle.enable_manager_review,
              enable_peer_review: !!cycle.enable_peer_review,
              enable_client_review: !!cycle.enable_client_review,
              completed: completed.map(normalizeEmployee),
              pending: pending.map(normalizeEmployee)
            };
          } catch (err) {
            console.error(`Error fetching status for cycle ${cycle.id}:`, err);
            return {
              id: cycle.id,
              name: cycle.name,
              enable_manager_review: !!cycle.enable_manager_review,
              enable_peer_review: !!cycle.enable_peer_review,
              enable_client_review: !!cycle.enable_client_review,
              completed: [],
              pending: []
            };
          }
        })
      );
      
      setCycleData(cyclesWithStatus);
    } catch (error) {
      console.error("Error fetching manager reviews:", error);
      toast.error("Failed to load your reviews");
    } finally {
      setLoading(false);
    }
  };

  const openReview = (cycleId: number, employeeId: number) => {
    navigate(`/manager-appraisals/${cycleId}/employee/${employeeId}`);
  };

  const handleOpenNominationModal = async (cycle: CycleWithStatus, employee: Employee) => {
    setSelectedReviewee(employee);
    setSelectedCycle(cycle);
    setShowNominationModal(true);
    setLoadingTeamMembers(true);

    try {
      const [teamMembers, nominationsResponse] = await Promise.all([
        getAllEmployeesForReviewsAssignment(),
        getPeerReviewNominationsAPI(cycle.id)
      ]);

      setManagerTeamMembers((teamMembers as any) || []);
      
      const revieweeNominations = (nominationsResponse.data || []).filter((nom: any) => {
        const revieweeId = typeof nom.reviewee === 'object' && nom.reviewee?.id 
          ? nom.reviewee.id 
          : nom.reviewee_id || nom.reviewee;
        return revieweeId === employee.id;
      });
      setExistingNominations(revieweeNominations);
      
      const existingReviewerIds = revieweeNominations.map((nom: any) => {
        if (typeof nom.reviewer === 'object' && nom.reviewer?.id) {
          return nom.reviewer.id;
        }
        return nom.reviewer_id || nom.reviewer;
      }).filter((id: number) => id !== undefined && id !== null);
      setSelectedReviewers(existingReviewerIds);
    } catch (err: any) {
      toast.error("Failed to load nomination data");
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const handleCloseNominationModal = () => {
    setShowNominationModal(false);
    setSelectedReviewee(null);
    setSelectedCycle(null);
    setSelectedReviewers([]);
    setExistingNominations([]);
  };

  const handleToggleReviewer = (reviewerId: number) => {
    setSelectedReviewers(prev => 
      prev.includes(reviewerId) 
        ? prev.filter(id => id !== reviewerId)
        : [...prev, reviewerId]
    );
  };

  const handleSubmitNominations = async () => {
    if (!selectedReviewee || !selectedCycle) {
      return;
    }

    try {
      setSubmitting(true);
      
      const existingReviewerIds = new Set(
        existingNominations.map((nom: any) => {
          if (typeof nom.reviewer === 'object' && nom.reviewer?.id) {
            return nom.reviewer.id;
          }
          return nom.reviewer_id || nom.reviewer;
        }).filter((id: number) => id !== undefined && id !== null)
      );
      const selectedReviewerIds = new Set(selectedReviewers);
      
      const toDelete = existingNominations.filter((nom: any) => {
        let reviewerId;
        if (typeof nom.reviewer === 'object' && nom.reviewer?.id) {
          reviewerId = nom.reviewer.id;
        } else {
          reviewerId = nom.reviewer_id || nom.reviewer;
        }
        return reviewerId && !selectedReviewerIds.has(reviewerId);
      });
      
      const toAdd = Array.from(selectedReviewerIds).filter(
        (reviewerId) => !existingReviewerIds.has(reviewerId)
      );
      
      const deletePromises = toDelete.map((nom: any) =>
        deletePeerReviewNominationAPI(selectedCycle.id, nom.id)
      );
      
      await Promise.all(deletePromises);
      
      if (toAdd.length > 0) {
        const newNominations = toAdd.map(reviewerId => ({
          reviewer_id: reviewerId,
          reviewee_id: selectedReviewee.id
        }));
        await createPeerReviewNominationsAPI(selectedCycle.id, newNominations);
      }
      
      const actionMessages = [];
      if (toDelete.length > 0) actionMessages.push(`removed ${toDelete.length}`);
      if (toAdd.length > 0) actionMessages.push(`added ${toAdd.length}`);
      
      if (actionMessages.length > 0) {
        toast.success(`Peer nominations ${actionMessages.join(' and ')} successfully`);
      } else {
        toast.info("No changes made");
      }
      
      handleCloseNominationModal();
      fetchManagerReviews();
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to update peer nominations");
    } finally {
      setSubmitting(false);
    }
  };

  const getAvailableReviewers = () => {
    if (!selectedReviewee) return [];
    return managerTeamMembers;
  };

  const toggleCardExpansion = async (cycleId: number, employeeId: number) => {
    const cardKey = `${cycleId}-${employeeId}`;
    const isExpanded = expandedCards.has(cardKey);
    
    if (isExpanded) {
      setExpandedCards(prev => {
        const newSet = new Set(prev);
        newSet.delete(cardKey);
        return newSet;
      });
    } else {
      setExpandedCards(prev => new Set(prev).add(cardKey));
      
      if (!employeeResponses[cardKey]) {
        try {
          setLoadingResponses(prev => ({ ...prev, [cardKey]: true }));
          const response = await getManagerTeamMemberResponsesAPI(cycleId, employeeId);
          setEmployeeResponses(prev => ({
            ...prev,
            [cardKey]: response.data || []
          }));
        } catch (err: any) {
          toast.error("Failed to load responses");
        } finally {
          setLoadingResponses(prev => ({ ...prev, [cardKey]: false }));
        }
      }
    }
  };

  const handleDownloadReport = async (e: React.MouseEvent, cycleId: number, employee: Employee, cycleName: string) => {
    e.stopPropagation();
    const key = `${cycleId}-${employee.id}`;
    setGeneratingPdf(prev => ({ ...prev, [key]: true }));

    try {
      let responses = employeeResponses[key];
      if (!responses) {
        const apiResponse = await getManagerTeamMemberResponsesAPI(cycleId, employee.id);
        responses = apiResponse.data || [];
      }

      if (responses && responses.length > 0) {
        generateAppraisalPDF(cycleName, employee, responses as any[]);
        toast.success("Report generated successfully");
      } else {
        toast.warning("No review data found for this employee.");
      }
    } catch (err) {
      console.error("Error generating report:", err);
      toast.error("Failed to generate report.");
    } finally {
      setGeneratingPdf(prev => ({ ...prev, [key]: false }));
    }
  };

  const calculateScore = (response: AppraisalResponse): number => {
    if (!response.question_responses || response.question_responses.length === 0) {
      return 0;
    }
    
    let totalScore = 0;
    let hasValidQuestions = false;
    
    response.question_responses.forEach((qr) => {
      const weightage = qr.question_weightage;
      const rating = qr.rating;
      
      if (weightage != null && weightage !== undefined && rating != null && rating !== undefined) {
        const questionScore = (Number(rating) / 5) * Number(weightage);
        totalScore += questionScore;
        hasValidQuestions = true;
      }
    });
    
    if (!hasValidQuestions) return 0;
    return Number(totalScore.toFixed(2));
  };

  const getReviewTypeLabel = (type: string) => {
    switch (type) {
      case 'SELF': return 'Self Review';
      case 'MANAGER': return 'Manager Review';
      case 'PEER': return 'Peer Review';
      case 'CLIENT': return 'Client Review';
      default: return type;
    }
  };

  const getReviewTypeColor = (type: string) => {
    switch (type) {
      case 'SELF': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
      case 'MANAGER': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400';
      case 'PEER': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400';
      case 'CLIENT': return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  if (loading) {
    return (
      <Card extra="p-6">
        <div className="flex items-center justify-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Loading manager reviews...</p>
        </div>
      </Card>
    );
  }

  return (
    <Card extra="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
          Manager Reviews
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Review appraisals for your direct reports
        </p>
      </div>

      {cycleData.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 dark:border-navy-600 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">
            No reviews available. You may not have any subordinates in active appraisal cycles.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {cycleData.map((cycle) => (
            <div key={cycle.id}>
              <h3 className="text-xl font-bold text-navy-700 dark:text-white mb-4">
                {cycle.name}
              </h3>

              {/* COMPLETED REVIEWS SECTION */}
              {cycle.completed && cycle.completed.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white mb-4">
                    Completed ({cycle.completed.length}):
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cycle.completed.map((employee) => (
                      <div
                        key={employee.id}
                        className="bg-white dark:bg-navy-800 border-2 border-green-200 dark:border-green-900/40 rounded-lg p-4 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex items-center gap-3 flex-1">
                            {employee.photo ? (
                              <img
                                src={employee.photo}
                                alt={employee.full_name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                  {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-navy-700 dark:text-white truncate">
                                {employee.full_name}
                              </p>
                              {employee.email && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {employee.email}
                                </p>
                              )}
                              {employee.designation && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  {employee.designation}
                                </p>
                              )}
                            </div>
                            <MdCheckCircle className="text-green-500 text-lg flex-shrink-0" />
                          </div>
                        </div>

                        <div className="space-y-2">
                          {cycle.enable_manager_review && (
                            <Button
                              varient="secondary"
                              onClick={() => openReview(cycle.id, employee.id)}
                              className="w-full flex items-center justify-center gap-2 text-sm"
                            >
                              <MdEdit className="h-4 w-4" />
                              View/Edit Review
                            </Button>
                          )}

                          <Button
                            varient="secondary"
                            onClick={(e) => handleDownloadReport(e, cycle.id, employee, cycle.name)}
                            disabled={generatingPdf[`${cycle.id}-${employee.id}`]}
                            className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                          >
                            {generatingPdf[`${cycle.id}-${employee.id}`] ? (
                              <>
                                <i className="fas fa-spinner fa-spin text-sm"></i>
                                <span>Generating...</span>
                              </>
                            ) : (
                              <>
                                <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                                <span>Download PDF Report</span>
                              </>
                            )}
                          </Button>

                          {cycle.enable_peer_review && (
                            <Button
                              varient="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenNominationModal(cycle, employee);
                              }}
                              className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                            >
                              <MdAdd className="h-4 w-4" />
                              <span>Nominate Peers</span>
                            </Button>
                          )}

                          <Button
                            varient="secondary"
                            onClick={() => toggleCardExpansion(cycle.id, employee.id)}
                            className="w-full flex items-center justify-center gap-2 text-sm"
                          >
                            {expandedCards.has(`${cycle.id}-${employee.id}`) ? (
                              <>
                                <MdExpandLess className="h-4 w-4" />
                                Hide Responses
                              </>
                            ) : (
                              <>
                                <MdExpandMore className="h-4 w-4" />
                                View Responses
                              </>
                            )}
                          </Button>
                        </div>

                        {expandedCards.has(`${cycle.id}-${employee.id}`) && (
                          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-navy-700">
                            {loadingResponses[`${cycle.id}-${employee.id}`] ? (
                              <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                                <i className="fas fa-spinner fa-spin text-lg mb-2"></i>
                                <p className="text-sm">Loading responses...</p>
                              </div>
                            ) : (
                              <div className="space-y-4">
                                {(employeeResponses[`${cycle.id}-${employee.id}`] || []).map((response, idx) => (
                                  <div key={idx} className="bg-gray-50 dark:bg-navy-900/30 p-3 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="flex items-center gap-2">
                                        <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getReviewTypeColor(response.reviewer_type)}`}>
                                          {getReviewTypeLabel(response.reviewer_type)}
                                        </span>
                                        {response.reviewer_name && response.reviewer_type === 'PEER' && (
                                          <span className="text-xs text-gray-600 dark:text-gray-400">
                                            by {response.reviewer_name}
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Score: {calculateScore(response).toFixed(2)}/5
                                      </span>
                                    </div>
                                    {response.question_responses && response.question_responses.length > 0 && (
                                      <div className="text-xs text-gray-600 dark:text-gray-400">
                                        <p>{response.question_responses.length} question(s) answered</p>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* PENDING REVIEWS SECTION */}
              {cycle.pending && cycle.pending.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-navy-700 dark:text-white mb-4">
                    Pending ({cycle.pending.length}):
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cycle.pending.map((employee) => (
                      <div
                        key={employee.id}
                        className="bg-white dark:bg-navy-800 border-2 border-yellow-200 dark:border-yellow-900/40 rounded-lg p-4 hover:shadow-lg transition-shadow"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div className="flex items-center gap-3 flex-1">
                            {employee.photo ? (
                              <img
                                src={employee.photo}
                                alt={employee.full_name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                                <span className="text-gray-600 dark:text-gray-400 font-semibold text-lg">
                                  {(employee.first_name?.[0] || '') + (employee.last_name?.[0] || '')}
                                </span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-navy-700 dark:text-white truncate">
                                {employee.full_name}
                              </p>
                              {employee.email && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {employee.email}
                                </p>
                              )}
                              {employee.designation && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  {employee.designation}
                                </p>
                              )}
                            </div>
                            <MdPending className="text-yellow-500 text-lg flex-shrink-0" />
                          </div>
                        </div>

                        <div className="space-y-2">
                          {cycle.enable_manager_review && (
                            <Button
                              varient="primary"
                              onClick={() => openReview(cycle.id, employee.id)}
                              className="w-full flex items-center justify-center gap-2 text-sm"
                            >
                              <MdEdit className="h-4 w-4" />
                              Start Review
                            </Button>
                          )}

                          <Button
                            varient="secondary"
                            onClick={(e) => handleDownloadReport(e, cycle.id, employee, cycle.name)}
                            disabled={generatingPdf[`${cycle.id}-${employee.id}`]}
                            className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                          >
                            {generatingPdf[`${cycle.id}-${employee.id}`] ? (
                              <>
                                <i className="fas fa-spinner fa-spin text-sm"></i>
                                <span>Generating...</span>
                              </>
                            ) : (
                              <>
                                <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                                <span>Download PDF Report</span>
                              </>
                            )}
                          </Button>

                          {cycle.enable_peer_review && (
                            <Button
                              varient="secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenNominationModal(cycle, employee);
                              }}
                              className="w-full flex items-center justify-center gap-2 py-2 text-sm"
                            >
                              <MdAdd className="h-4 w-4" />
                              <span>Nominate Peers</span>
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!cycle.completed || cycle.completed.length === 0) && (!cycle.pending || cycle.pending.length === 0) && (
                <div className="bg-gray-50 dark:bg-gray-900/30 border-2 border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    No subordinates assigned to this cycle yet
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
                    {(cycle.enable_peer_review || cycle.enable_client_review) && (
                      <>
                        <Button
                          varient="secondary"
                          onClick={(e) => handleDownloadReport(e, cycle.id, {id: 0, first_name: 'Report', last_name: 'Download', full_name: 'Cycle Report', email: '', designation: ''}, cycle.name)}
                          disabled={generatingPdf[`${cycle.id}-0`]}
                          className="flex items-center justify-center gap-2 px-4 py-2 text-sm"
                          title="Download Cycle Report"
                        >
                          {generatingPdf[`${cycle.id}-0`] ? (
                            <>
                              <i className="fas fa-spinner fa-spin text-sm"></i>
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <MdPictureAsPdf className="h-5 w-5 text-red-500" />
                              <span>Download PDF</span>
                            </>
                          )}
                        </Button>
                        {cycle.enable_peer_review && (
                          <Button
                            varient="secondary"
                            onClick={() => toast.info('Assign employees to this cycle to nominate peer reviewers')}
                            className="flex items-center justify-center gap-2 px-4 py-2 text-sm opacity-50"
                            disabled={true}
                          >
                            <MdAdd className="h-4 w-4" />
                            <span>No Employees</span>
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showNominationModal && selectedReviewee && selectedCycle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div 
            ref={modalRef}
            className="bg-white dark:bg-navy-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                    Nominate Peers for Review
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Select team members who will review <span className="font-semibold">{selectedReviewee.full_name || `${selectedReviewee.first_name} ${selectedReviewee.last_name}`}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    Cycle: {selectedCycle.name}
                  </p>
                </div>
                <button
                  onClick={handleCloseNominationModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <i className="fas fa-times text-gray-600 dark:text-gray-300"></i>
                </button>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 p-6">
              {loadingTeamMembers ? (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                  <i className="fas fa-spinner fa-spin text-2xl mb-2"></i>
                  <p>Loading team members...</p>
                </div>
              ) : getAvailableReviewers().length === 0 ? (
                <div className="text-center py-10 text-gray-500 dark:text-gray-400">
                  No available reviewers from your team.
                </div>
              ) : (
                <div className="space-y-3">
                  {getAvailableReviewers().map((reviewer: Employee) => (
                    <label
                      key={reviewer.id}
                      className="flex items-center gap-3 p-4 border border-gray-200 dark:border-navy-700 rounded-lg hover:bg-gray-50 dark:hover:bg-navy-700 cursor-pointer transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={selectedReviewers.includes(reviewer.id)}
                        onChange={() => handleToggleReviewer(reviewer.id)}
                        className="w-5 h-5 text-brand-600 border-gray-300 rounded focus:ring-brand-500"
                      />
                      <div className="flex items-center gap-3 flex-1">
                        {reviewer.photo ? (
                          <img
                            src={reviewer.photo}
                            alt={reviewer.full_name || "Employee"}
                            className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 dark:border-navy-600"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-navy-700 flex items-center justify-center border-2 border-gray-200 dark:border-navy-600">
                            <span className="text-gray-600 dark:text-gray-400 font-semibold text-sm">
                              {(reviewer.first_name?.[0] || '') + (reviewer.last_name?.[0] || '')}
                            </span>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-navy-700 dark:text-white truncate">
                            {reviewer.full_name || `${reviewer.first_name || ''} ${reviewer.last_name || ''}`.trim() || 'Unknown'}
                          </p>
                          {reviewer.email && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {reviewer.email}
                            </p>
                          )}
                          {reviewer.designation && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                              {reviewer.designation}
                            </p>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 flex justify-end gap-3">
              <Button
                varient="secondary"
                onClick={handleCloseNominationModal}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                varient="primary"
                onClick={handleSubmitNominations}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : `Save Changes ${selectedReviewers.length > 0 ? `(${selectedReviewers.length} selected)` : ''}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default ManagerReviewTab;
